import {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionTrigger
} from '@radix-ui/react-accordion';
import { ChevronDownIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

export interface ParsedCimBullet {
  page: number;
  text: string;
}

export interface ParsedCimData {
  [key: string]: unknown;
  avgCompanyTenure?: number;
  avgIndustryTenure?: number;
  barriersExp?: string;
  barriersYN?: string;
  boardExperience?: string;
  boardIndependent?: number;
  businessCriticalityExp?: string;
  businessCriticalityYN?: string;
  businessDesc?: string;
  companyName?: string;
  competitiveAdvExp?: string;
  competitiveAdvYN?: string;
  customerCommentary?: string;
  employeeCount?: number;
  fdoPercent?: number;
  financialPeriods?: ParsedCimFinancialPeriod[];
  fragmentation?: string;
  fundInvestingOutOf?: string;
  gicsIndustry?: string;
  headquarters?: string;
  headwinds?: string;
  highlights?: ParsedCimHighlight[];
  industry?: string;
  industryGrowthRate?: number;
  investmentThesis?: string;
  laborSupply?: string;
  managementNotes?: string;
  marketLeaderExp?: string;
  marketLeaderYN?: string;
  mgmtCount?: number;
  operatingPartners?: string;
  products?: ParsedCimProduct[];
  revenueUS?: number;
  risks?: ParsedCimRisk[];
  risksNote?: string;
  sam?: number;
  securities?: string;
  sponsorAUM?: number;
  sponsorDryPowder?: number;
  sponsorNotes?: string;
  sponsorPrincipals?: string;
  sponsorTrackRecord?: string;
  supplierCommentary?: string;
  tailwinds?: string;
  tam?: number;
  top10Concentration?: number;
  totalDebt?: number;
  totalEquity?: number;
  transactionBackground?: string;
  transactionDescription?: string;
  transactionType?: string;
  turnoverPercent?: number;
  unionized?: string;
  yearFounded?: number;
}

export interface ParsedCimFinancialPeriod {
  capex: number;
  cogs: number;
  ebitda: number;
  nwc: number;
  period: string;
  revenue: number;
  sga: number;
}

export interface ParsedCimHighlight {
  bullets: ParsedCimBullet[];
  title: string;
}

export interface ParsedCimMitigant {
  page: number;
  text: string;
}

export interface ParsedCimProduct {
  description: string;
  name: string;
  revenue: number;
}

export interface ParsedCimRisk {
  mitigants: ParsedCimMitigant[];
  risk: string;
}

interface ParseCimModalProps {
  data: null | ParsedCimData;
  onApply?: (data: ParsedCimData) => Promise<void>;
  onDataChange?: (data: ParsedCimData) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

const num = (v: unknown): number =>
  typeof v === 'number' && !Number.isNaN(v) ? v : 0;
const str = (v: unknown): string => (v != null ? String(v) : '');

export function ParseCimModal({
  data,
  onApply,
  onDataChange,
  onOpenChange,
  open
}: ParseCimModalProps) {
  const [applying, setApplying] = useState(false);
  const [localData, setLocalData] = useState<null | ParsedCimData>(null);

  // Keep a local copy so typing doesn't update parent on every keystroke
  useEffect(() => {
    if (open && data) {
      setLocalData(JSON.parse(JSON.stringify(data)) as ParsedCimData);
    }
  }, [open, data]);

  const handleOpenChange = (next: boolean) => {
    if (!next && localData && onDataChange) {
      onDataChange(localData);
    }
    onOpenChange(next);
  };

  if (!data) return null;

  const editingData = localData ?? data;

  const set = (path: string, value: unknown) => {
    setLocalData(
      setIn(
        editingData as unknown as Record<string, unknown>,
        path,
        value
      ) as ParsedCimData
    );
  };

  const financialPeriods = editingData.financialPeriods ?? [];
  const highlights = editingData.highlights ?? [];
  const risks = editingData.risks ?? [];
  const products = editingData.products ?? [];

  return (
    <Dialog
      onOpenChange={handleOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex max-h-[90vh] w-[900px] max-w-[calc(100%-40px)] flex-col overflow-hidden"
      >
        <DialogHeader>
          <DialogTitle>Edit parsed CIM data</DialogTitle>
          <DialogDescription>
            Review and edit the parsed CIM fields. List data is shown in tables.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="no-scrollbar -mx-1 flex-1 overflow-y-auto px-1">
            <Accordion
              className="w-full space-y-2"
              defaultValue={[
                'company',
                'financials',
                'highlights',
                'risks',
                'products',
                'other'
              ]}
              type="multiple"
            >
              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="company"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Company & transaction
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down space-y-3 overflow-hidden pt-0 pb-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Company name</FieldLabel>
                        <Input
                          onChange={e => {
                            set('companyName', e.target.value);
                          }}
                          value={str(editingData.companyName)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Revenue (US $M)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('revenueUS', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.revenueUS) || ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Industry</FieldLabel>
                        <Input
                          onChange={e => {
                            set('industry', e.target.value);
                          }}
                          value={str(editingData.industry)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Securities</FieldLabel>
                        <Input
                          onChange={e => {
                            set('securities', e.target.value);
                          }}
                          value={str(editingData.securities)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Transaction type</FieldLabel>
                        <Input
                          onChange={e => {
                            set('transactionType', e.target.value);
                          }}
                          value={str(editingData.transactionType)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>GICS industry</FieldLabel>
                        <Input
                          onChange={e => {
                            set('gicsIndustry', e.target.value);
                          }}
                          value={str(editingData.gicsIndustry)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Headquarters</FieldLabel>
                        <Input
                          onChange={e => {
                            set('headquarters', e.target.value);
                          }}
                          value={str(editingData.headquarters)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Year founded</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'yearFounded',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.yearFounded) || ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Total debt ($M)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('totalDebt', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.totalDebt) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Total equity ($M)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('totalEquity', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.totalEquity) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Top 10 concentration %</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'top10Concentration',
                              parseFloat(e.target.value) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.top10Concentration) ?? ''}
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Business description</FieldLabel>
                      <Textarea
                        className="min-h-[80px]"
                        onChange={e => {
                          set('businessDesc', e.target.value);
                        }}
                        value={str(editingData.businessDesc)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Transaction background</FieldLabel>
                      <Textarea
                        className="min-h-[80px]"
                        onChange={e => {
                          set('transactionBackground', e.target.value);
                        }}
                        value={str(editingData.transactionBackground)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Transaction description</FieldLabel>
                      <Textarea
                        className="min-h-[80px]"
                        onChange={e => {
                          set('transactionDescription', e.target.value);
                        }}
                        value={str(editingData.transactionDescription)}
                      />
                    </Field>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="financials"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Financial periods ({financialPeriods.length})
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pt-0 pb-3">
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead>Revenue</TableHead>
                          <TableHead>COGS</TableHead>
                          <TableHead>SGA</TableHead>
                          <TableHead>EBITDA</TableHead>
                          <TableHead>Capex</TableHead>
                          <TableHead>NWC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {financialPeriods.map((row, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Input
                                className="h-8 w-24"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.period`,
                                    e.target.value
                                  );
                                }}
                                value={str(row.period)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.revenue`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.revenue ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.cogs`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.cogs ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.sga`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.sga ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.ebitda`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.ebitda ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.capex`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.capex ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-20"
                                onChange={e => {
                                  set(
                                    `financialPeriods.${i}.nwc`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={row.nwc ?? ''}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="highlights"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Highlights ({highlights.length})
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down space-y-4 overflow-hidden pt-0 pb-3">
                  {highlights.map((h, hi) => (
                    <div
                      className="space-y-2 rounded-md border p-3"
                      key={hi}
                    >
                      <Field>
                        <FieldLabel>Title</FieldLabel>
                        <Input
                          onChange={e => {
                            set(`highlights.${hi}.title`, e.target.value);
                          }}
                          value={str(h.title)}
                        />
                      </Field>
                      <Label className="pt-2">Bullets</Label>
                      <div className="overflow-x-auto rounded border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="min-w-[200px]">
                                Text
                              </TableHead>
                              <TableHead className="w-20">Page</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(h.bullets ?? []).map((b, bi) => (
                              <TableRow key={bi}>
                                <TableCell>
                                  <Textarea
                                    className="min-h-[60px] resize-y"
                                    onChange={e => {
                                      set(
                                        `highlights.${hi}.bullets.${bi}.text`,
                                        e.target.value
                                      );
                                    }}
                                    value={str(b.text)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    className="h-8 w-16"
                                    onChange={e => {
                                      set(
                                        `highlights.${hi}.bullets.${bi}.page`,
                                        parseInt(e.target.value, 10) || 0
                                      );
                                    }}
                                    type="number"
                                    value={b.page ?? ''}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="risks"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Risks ({risks.length})
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down space-y-4 overflow-hidden pt-0 pb-3">
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Risks note</FieldLabel>
                      <Textarea
                        className="min-h-[60px]"
                        onChange={e => {
                          set('risksNote', e.target.value);
                        }}
                        value={str(editingData.risksNote)}
                      />
                    </Field>
                  </div>
                  {risks.map((r, ri) => (
                    <div
                      className="space-y-2 rounded-md border p-3"
                      key={ri}
                    >
                      <Field>
                        <FieldLabel>Risk</FieldLabel>
                        <Input
                          onChange={e => {
                            set(`risks.${ri}.risk`, e.target.value);
                          }}
                          value={str(r.risk)}
                        />
                      </Field>
                      <Label className="pt-2">Mitigants</Label>
                      <div className="overflow-x-auto rounded border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="min-w-[200px]">
                                Text
                              </TableHead>
                              <TableHead className="w-20">Page</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(r.mitigants ?? []).map((m, mi) => (
                              <TableRow key={mi}>
                                <TableCell>
                                  <Textarea
                                    className="min-h-[60px] resize-y"
                                    onChange={e => {
                                      set(
                                        `risks.${ri}.mitigants.${mi}.text`,
                                        e.target.value
                                      );
                                    }}
                                    value={str(m.text)}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    className="h-8 w-16"
                                    onChange={e => {
                                      set(
                                        `risks.${ri}.mitigants.${mi}.page`,
                                        parseInt(e.target.value, 10) || 0
                                      );
                                    }}
                                    type="number"
                                    value={m.page ?? ''}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="products"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Products ({products.length})
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden pt-0 pb-3">
                  <div className="overflow-x-auto rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead className="w-28">Revenue</TableHead>
                          <TableHead className="min-w-[200px]">
                            Description
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {products.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell>
                              <Input
                                className="h-8"
                                onChange={e => {
                                  set(`products.${i}.name`, e.target.value);
                                }}
                                value={str(p.name)}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 w-24"
                                onChange={e => {
                                  set(
                                    `products.${i}.revenue`,
                                    parseFloat(e.target.value) || 0
                                  );
                                }}
                                type="number"
                                value={p.revenue ?? ''}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                className="h-8 min-w-[200px]"
                                onChange={e => {
                                  set(
                                    `products.${i}.description`,
                                    e.target.value
                                  );
                                }}
                                value={str(p.description)}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem
                className="rounded-md border px-3 py-1"
                value="other"
              >
                <AccordionHeader>
                  <AccordionTrigger className="group flex w-full items-center justify-between py-3 text-left font-medium hover:no-underline [&[data-state=open]>svg]:rotate-180">
                    Commentary, team & market
                    <ChevronDownIcon
                      aria-hidden
                      className="size-4 shrink-0 transition-transform duration-200"
                    />
                  </AccordionTrigger>
                </AccordionHeader>
                <AccordionContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down space-y-3 overflow-hidden pt-0 pb-3">
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Customer commentary</FieldLabel>
                      <Textarea
                        className="min-h-[80px]"
                        onChange={e => {
                          set('customerCommentary', e.target.value);
                        }}
                        value={str(editingData.customerCommentary)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Supplier commentary</FieldLabel>
                      <Textarea
                        className="min-h-[80px]"
                        onChange={e => {
                          set('supplierCommentary', e.target.value);
                        }}
                        value={str(editingData.supplierCommentary)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Mgmt count</FieldLabel>
                        <Input
                          onChange={e => {
                            set('mgmtCount', parseInt(e.target.value, 10) || 0);
                          }}
                          type="number"
                          value={num(editingData.mgmtCount) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Avg industry tenure</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'avgIndustryTenure',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.avgIndustryTenure) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Avg company tenure</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'avgCompanyTenure',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.avgCompanyTenure) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>FDO %</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'fdoPercent',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.fdoPercent) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Employee count</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'employeeCount',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.employeeCount) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Turnover %</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'turnoverPercent',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.turnoverPercent) ?? ''}
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Unionized</FieldLabel>
                      <Input
                        onChange={e => {
                          set('unionized', e.target.value);
                        }}
                        value={str(editingData.unionized)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Labor supply</FieldLabel>
                      <Input
                        onChange={e => {
                          set('laborSupply', e.target.value);
                        }}
                        value={str(editingData.laborSupply)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Management notes</FieldLabel>
                      <Textarea
                        className="min-h-[60px]"
                        onChange={e => {
                          set('managementNotes', e.target.value);
                        }}
                        value={str(editingData.managementNotes)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Sponsor AUM</FieldLabel>
                        <Input
                          onChange={e => {
                            set('sponsorAUM', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.sponsorAUM) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Sponsor dry powder</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'sponsorDryPowder',
                              parseFloat(e.target.value) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.sponsorDryPowder) ?? ''}
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Sponsor track record</FieldLabel>
                      <Textarea
                        className="min-h-[60px]"
                        onChange={e => {
                          set('sponsorTrackRecord', e.target.value);
                        }}
                        value={str(editingData.sponsorTrackRecord)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Sponsor principals</FieldLabel>
                      <Input
                        onChange={e => {
                          set('sponsorPrincipals', e.target.value);
                        }}
                        value={str(editingData.sponsorPrincipals)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Fund investing out of</FieldLabel>
                      <Input
                        onChange={e => {
                          set('fundInvestingOutOf', e.target.value);
                        }}
                        value={str(editingData.fundInvestingOutOf)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Sponsor notes</FieldLabel>
                      <Textarea
                        className="min-h-[60px]"
                        onChange={e => {
                          set('sponsorNotes', e.target.value);
                        }}
                        value={str(editingData.sponsorNotes)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Board independent</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'boardIndependent',
                              parseInt(e.target.value, 10) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.boardIndependent) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>TAM</FieldLabel>
                        <Input
                          onChange={e => {
                            set('tam', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.tam) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>SAM</FieldLabel>
                        <Input
                          onChange={e => {
                            set('sam', parseFloat(e.target.value) || 0);
                          }}
                          type="number"
                          value={num(editingData.sam) ?? ''}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Industry growth rate %</FieldLabel>
                        <Input
                          onChange={e => {
                            set(
                              'industryGrowthRate',
                              parseFloat(e.target.value) || 0
                            );
                          }}
                          type="number"
                          value={num(editingData.industryGrowthRate) ?? ''}
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Board experience</FieldLabel>
                      <Input
                        onChange={e => {
                          set('boardExperience', e.target.value);
                        }}
                        value={str(editingData.boardExperience)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Operating partners</FieldLabel>
                      <Input
                        onChange={e => {
                          set('operatingPartners', e.target.value);
                        }}
                        value={str(editingData.operatingPartners)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Fragmentation</FieldLabel>
                      <Input
                        onChange={e => {
                          set('fragmentation', e.target.value);
                        }}
                        value={str(editingData.fragmentation)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Headwinds</FieldLabel>
                      <Input
                        onChange={e => {
                          set('headwinds', e.target.value);
                        }}
                        value={str(editingData.headwinds)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Tailwinds</FieldLabel>
                      <Input
                        onChange={e => {
                          set('tailwinds', e.target.value);
                        }}
                        value={str(editingData.tailwinds)}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Business criticality (Y/N)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('businessCriticalityYN', e.target.value);
                          }}
                          value={str(editingData.businessCriticalityYN)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Barriers (Y/N)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('barriersYN', e.target.value);
                          }}
                          value={str(editingData.barriersYN)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Competitive advantage (Y/N)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('competitiveAdvYN', e.target.value);
                          }}
                          value={str(editingData.competitiveAdvYN)}
                        />
                      </Field>
                    </div>
                    <div className="space-y-2">
                      <Field>
                        <FieldLabel>Market leader (Y/N)</FieldLabel>
                        <Input
                          onChange={e => {
                            set('marketLeaderYN', e.target.value);
                          }}
                          value={str(editingData.marketLeaderYN)}
                        />
                      </Field>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Business criticality explanation</FieldLabel>
                      <Input
                        onChange={e => {
                          set('businessCriticalityExp', e.target.value);
                        }}
                        value={str(editingData.businessCriticalityExp)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Barriers explanation</FieldLabel>
                      <Input
                        onChange={e => {
                          set('barriersExp', e.target.value);
                        }}
                        value={str(editingData.barriersExp)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Competitive advantage explanation</FieldLabel>
                      <Input
                        onChange={e => {
                          set('competitiveAdvExp', e.target.value);
                        }}
                        value={str(editingData.competitiveAdvExp)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Market leader explanation</FieldLabel>
                      <Input
                        onChange={e => {
                          set('marketLeaderExp', e.target.value);
                        }}
                        value={str(editingData.marketLeaderExp)}
                      />
                    </Field>
                  </div>
                  <div className="space-y-2">
                    <Field>
                      <FieldLabel>Investment thesis</FieldLabel>
                      <Textarea
                        className="min-h-[120px]"
                        onChange={e => {
                          set('investmentThesis', e.target.value);
                        }}
                        value={str(editingData.investmentThesis)}
                      />
                    </Field>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          {onApply && (
            <div className="bg-background sticky bottom-0 flex shrink-0 justify-end border-t px-1 py-3">
              <Button
                disabled={applying}
                onClick={() => {
                  setApplying(true);
                  void onApply(editingData).finally(() => {
                    setApplying(false);
                  });
                }}
              >
                {applying && (
                  <Loader2Icon className="mr-2 size-4 animate-spin" />
                )}
                Apply
              </Button>
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function setIn(
  obj: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const keys = path.split('.');
  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }
  const [head, ...rest] = keys;
  const restPath = rest.join('.');
  const current = obj[head];
  const idx = parseInt(rest[0], 10);
  if (rest.length >= 1 && !Number.isNaN(idx) && Array.isArray(current)) {
    const copy = current.map((item, i) =>
      i === idx
        ? rest.length === 1
          ? value
          : setIn(
              typeof item === 'object' && item !== null
                ? (item as Record<string, unknown>)
                : {},
              rest.slice(1).join('.'),
              value
            )
        : item
    );
    return { ...obj, [head]: copy };
  }
  const child =
    typeof current === 'object' && current !== null
      ? (current as Record<string, unknown>)
      : {};
  return {
    ...obj,
    [head]: setIn(child, restPath, value)
  };
}
